
import { useEffect, useMemo, useState } from "react";
import {
  ChevronDown,
  Copy,
  Download,
  ExternalLink,
  Search,
  ChevronRight,
} from "lucide-react";
import configData from "../config.json";
import { getToken } from "../utils/auth";

const DASHBOARD_URL = "https://catch-dashboard-re4b66cc5a-ez.a.run.app/";

function safeJsonStringify(value) {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function parseDateMaybe(value) {
  const d = value ? new Date(value) : null;
  return d && !Number.isNaN(d.getTime()) ? d : null;
}

function formatDateTime(value) {
  const d = parseDateMaybe(value);
  if (!d) return "—";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(d);
}

function formatRelative(value) {
  const d = parseDateMaybe(value);
  if (!d) return "";
  const diffMs = Date.now() - d.getTime();
  const abs = Math.abs(diffMs);
  const minutes = Math.round(abs / 60000);
  const hours = Math.round(abs / 3600000);
  const days = Math.round(abs / 86400000);
  const suffix = diffMs >= 0 ? "ago" : "from now";
  if (minutes < 2) return `just now`;
  if (minutes < 60) return `${minutes}m ${suffix}`;
  if (hours < 48) return `${hours}h ${suffix}`;
  return `${days}d ${suffix}`;
}

function formatDarkMode(darkMode) {
  if (darkMode === true) return "true";
  if (darkMode === false) return "false";
  return "—";
}

function formatBattery(battery) {
  if (!battery) return "—";
  if (typeof battery === "string") return battery;
  if (typeof battery === "object") {
    const level = battery?.level ?? "—";
    const chargingText =
      battery?.charging === true ? " (charging)" : battery?.charging === false ? " (not charging)" : "";
    return `${level}${chargingText}`;
  }
  return "—";
}

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback for older browsers / permissions.
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

function downloadJson(filename, value) {
  const blob = new Blob([safeJsonStringify(value)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function normalizeLogs(input) {
  if (!Array.isArray(input)) return [];
  return input
    .map((l) => (l && typeof l === "object" ? l : null))
    .filter(Boolean)
    .map((l) => {
      const ts = l?.fields?.timestamp ?? l?.timestamp ?? l?.createTime ?? l?.updateTime;
      return {
        ...l,
        _timestamp: ts,
      };
    });
}

function extractLogsFromServerResponse(json) {
  if (Array.isArray(json)) return json;
  if (json && typeof json === "object") {
    if (Array.isArray(json.logs)) return json.logs;
    if (Array.isArray(json.scannerLogs)) return json.scannerLogs;
    if (Array.isArray(json.data)) return json.data;
  }
  return [];
}

export function Step4Measure({
  FadeIn,
  notify,
  scannerLogs,
  // kept for backward compatibility (passed by `new_mia.jsx` today)
  takeOverScreenStatus,
  setTakeOverScreenStatus,
  screenData,
  setScreenData,
}) {
  const [range, setRange] = useState("24h");
  const [query, setQuery] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [pastedJson, setPastedJson] = useState("");
  const [localLogs, setLocalLogs] = useState([]);
  const [serverLogs, setServerLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [copyState, setCopyState] = useState({}); // id -> "copied"

  const fetchScannerLogs = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const token = getToken?.();
      const res = await fetch(`${configData.DEMO_URL}/ScannerLogs?limit=200`, {
        method: "GET",
        headers: {
          ...(token ? { "x-access-token": token } : {}),
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        let msg = `Failed to fetch scanner logs`;
        try {
          const body = await res.json();
          if (body?.message) msg = body.message;
        } catch (_) {}
        throw new Error(msg);
      }

      const json = await res.json();
      const extracted = extractLogsFromServerResponse(json);
      setServerLogs(extracted);
      return true;
    } catch (e) {
      notify?.(e?.message || "Failed to fetch scanner logs", "error");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // If parent passes logs, don't fetch here.
    if (scannerLogs?.length) return;
    // If user pasted logs locally, don't overwrite them.
    if (localLogs?.length) return;
    fetchScannerLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scannerLogs]);

  const logs = useMemo(() => {
    const base = scannerLogs?.length ? scannerLogs : serverLogs?.length ? serverLogs : localLogs;
    const normalized = normalizeLogs(base);
    return normalized.sort((a, b) => {
      const da = parseDateMaybe(a?._timestamp)?.getTime() ?? 0;
      const db = parseDateMaybe(b?._timestamp)?.getTime() ?? 0;
      return db - da;
    });
  }, [scannerLogs, serverLogs, localLogs]);

  const filteredLogs = logs;

  const summary = useMemo(() => {
    const total = filteredLogs.length;
    const mostRecent = filteredLogs[0]?._timestamp ?? null;
    const osCounts = filteredLogs.reduce((acc, l) => {
      const os = l?.fields?.user?.os ?? "Unknown";
      acc[os] = (acc[os] ?? 0) + 1;
      return acc;
    }, {});
    const topOs = Object.entries(osCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 2)
      .map(([k, v]) => `${k} (${v})`)
      .join(", ");

    return {
      total,
      mostRecent,
      topOs: topOs || "—",
    };
  }, [filteredLogs]);

  const handlePasteLoad = () => {
    try {
      const parsed = JSON.parse(pastedJson);
      if (!Array.isArray(parsed)) throw new Error("Expected an array of log objects.");
      setLocalLogs(parsed);
      setServerLogs([]);
      setExpandedId(null);
      notify?.("Loaded logs from pasted JSON", "success");
    } catch (e) {
      notify?.(e?.message || "Invalid JSON", "error");
    }
  };

  const onCopy = async (key, text) => {
    const ok = await copyToClipboard(text);
    if (!ok) {
      notify?.("Copy failed", "error");
      return;
    }
    setCopyState((p) => ({ ...p, [key]: "copied" }));
    window.setTimeout(() => {
      setCopyState((p) => {
        const next = { ...p };
        delete next[key];
        return next;
      });
    }, 1200);
  };

  return (
    <FadeIn className="p-8 bg-white flex flex-col">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
        <div>
          <h3 className="text-xl font-bold text-blue-900">Scan Logs</h3>
          <div className="text-sm text-blue-700 mt-1">
            Most recent: <span className="font-medium text-blue-900">{formatDateTime(summary.mostRecent)}</span>{" "}
            <span className="text-blue-600">({formatRelative(summary.mostRecent)})</span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            className="text-sm bg-blue-100 hover:bg-blue-200 text-blue-700 px-4 py-1.5 rounded-lg font-medium transition-colors border border-blue-200 shadow-sm inline-flex items-center gap-2"
            onClick={() => window.open(DASHBOARD_URL, "_blank")}
          >
            <ExternalLink size={16} />
            To Dashboard
          </button>

          <div className="relative">
            <select
              value={range}
              onChange={(e) => setRange(e.target.value)}
              className="appearance-none border border-blue-300 rounded text-sm py-1.5 pl-3 pr-8 bg-blue-50 text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
            </select>
            <ChevronDown size={14} className="absolute right-2 top-2.5 text-blue-500 pointer-events-none" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
          <div className="text-sm text-blue-700 mb-1">Scans</div>
          <div className="text-3xl font-bold text-blue-900">{summary.total.toLocaleString()}</div>
          <div className="text-xs text-blue-600 mt-1">in selected time range</div>
        </div>
        <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
          <div className="text-sm text-blue-700 mb-1">Top OS</div>
          <div className="text-lg font-bold text-blue-900 leading-6">{summary.topOs}</div>
          <div className="text-xs text-blue-600 mt-1">based on `fields.user.os`</div>
        </div>
        <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
          <div className="text-sm text-blue-700 mb-1">Data source</div>
          <div className="text-lg font-bold text-blue-900 leading-6">
            {scannerLogs?.length
              ? "Live props"
              : localLogs?.length
                ? "Pasted JSON"
                : serverLogs?.length
                  ? "Server (/ScannerLogs)"
                  : isLoading
                    ? "Loading…"
                    : "—"}
          </div>
          <div className="text-xs text-blue-600 mt-1">Step can render either</div>
        </div>
      </div>

      <div className="mb-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-2.5 text-blue-500" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search id, OS, dark mode, battery, userAgent…"
            className="w-full border border-blue-300 rounded text-sm py-2 pl-9 pr-3 bg-white text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="flex items-center gap-3">
          <button
            className="text-sm bg-white hover:bg-blue-50 text-blue-700 px-3 py-2 rounded-lg font-medium transition-colors border border-blue-200 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
            onClick={() => fetchScannerLogs()}
            disabled={isLoading}
          >
            {isLoading ? "Refreshing…" : "Refresh"}
          </button>
          <button
            className="text-sm bg-white hover:bg-blue-50 text-blue-700 px-3 py-2 rounded-lg font-medium transition-colors border border-blue-200 shadow-sm"
            onClick={() => {
              setQuery("");
              setExpandedId(null);
            }}
          >
            Clear
          </button>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex-1 flex flex-col min-h-[280px]">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-bold text-blue-900">Latest scans</h4>
          <div className="text-xs text-blue-700">{filteredLogs.length.toLocaleString()} rows</div>
        </div>

        {filteredLogs.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
            <div className="text-blue-900 font-semibold">No logs to show</div>
            <div className="text-sm text-blue-700 mt-1">
              Pass `scannerLogs` into this step, or paste JSON logs below to preview the UI.
            </div>

            <div className="w-full max-w-3xl mt-4">
              <textarea
                value={pastedJson}
                onChange={(e) => setPastedJson(e.target.value)}
                placeholder='Paste the logs JSON array here (e.g. [{"id":"scan_...","fields":{...}}])'
                className="w-full h-40 text-xs font-mono border border-blue-300 rounded-lg p-3 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex justify-end mt-2">
                <button
                  className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors shadow-sm"
                  onClick={handlePasteLoad}
                >
                  Load pasted logs
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-auto custom-scrollbar rounded-lg">
            <div className="min-w-[820px]">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs font-semibold text-blue-800 border-b border-blue-200 sticky top-0 bg-blue-50/95 backdrop-blur">
                <div className="col-span-4">Time</div>
                <div className="col-span-2">OS / Lang</div>
                <div className="col-span-2">Dark mode</div>
                <div className="col-span-2">Battery</div>
                <div className="col-span-2">User agent</div>
              </div>

              {filteredLogs.map((l) => {
                const isOpen = expandedId === l.id;
                const ts = l?._timestamp;
                const user = l?.fields?.user ?? {};
                const hw = l?.fields?.hardware ?? {};
                const net = l?.fields?.network ?? {};
                const prefs = l?.fields?.preferences ?? {};

                return (
                  <div key={l.id} className="border-b border-blue-200">
                    <button
                      className="w-full text-left grid grid-cols-12 gap-2 px-3 py-2 hover:bg-blue-100/60 transition-colors"
                      onClick={() => setExpandedId(isOpen ? null : l.id)}
                    >
                      <div className="col-span-4 flex items-start gap-2">
                        <ChevronRight
                          size={16}
                          className={`mt-0.5 text-blue-700 transition-transform ${isOpen ? "rotate-90" : ""}`}
                        />
                        <div>
                          <div className="text-sm font-semibold text-blue-900 leading-5">{formatDateTime(ts)}</div>
                          <div className="text-xs text-blue-700 mt-0.5 flex items-center gap-2">
                            <span className="font-mono">{l.id}</span>
                            <span className="text-blue-600">•</span>
                            <span>{formatRelative(ts)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="col-span-2 text-sm text-blue-900">
                        <div className="font-medium">{user?.os ?? "—"}</div>
                        <div className="text-xs text-blue-700">{user?.language ?? "—"}</div>
                      </div>

                      <div className="col-span-2 text-sm text-blue-900">
                        <div className="font-medium">{formatDarkMode(prefs?.darkMode)}</div>
                        <div className="text-xs text-blue-700">Dark mode</div>
                      </div>

                      <div className="col-span-2 text-sm text-blue-900">
                        <div className="font-medium">{formatBattery(hw?.battery)}</div>
                        <div className="text-xs text-blue-700">Battery</div>
                      </div>

                      <div className="col-span-2 text-sm text-blue-900">
                        <div className="font-medium truncate" title={user?.userAgent ?? ""}>
                          {user?.userAgent ?? "—"}
                        </div>
                        <div className="text-xs text-blue-700">User agent</div>
                      </div>
                    </button>

                    {isOpen ? (
                      <div className="px-3 pb-3">
                        <div className="bg-white border border-blue-200 rounded-lg p-3">
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                            <div className="text-sm font-semibold text-blue-900">Details</div>
                            <div className="flex flex-wrap items-center gap-2">
                              <button
                                className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-800 px-2.5 py-1.5 rounded border border-blue-200 inline-flex items-center gap-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onCopy(`id:${l.id}`, l.id);
                                }}
                              >
                                <Copy size={14} />
                                {copyState[`id:${l.id}`] ? "Copied" : "Copy id"}
                              </button>
                              <button
                                className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-800 px-2.5 py-1.5 rounded border border-blue-200 inline-flex items-center gap-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onCopy(`json:${l.id}`, safeJsonStringify(l));
                                }}
                              >
                                <Copy size={14} />
                                {copyState[`json:${l.id}`] ? "Copied" : "Copy JSON"}
                              </button>
                              <button
                                className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-800 px-2.5 py-1.5 rounded border border-blue-200 inline-flex items-center gap-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  downloadJson(`${l.id}.json`, l);
                                }}
                              >
                                <Download size={14} />
                                Download
                              </button>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                            <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-3">
                              <div className="text-xs font-semibold text-blue-800 mb-2">User</div>
                              <div className="text-xs text-slate-900 space-y-1">
                                <div><span className="text-blue-700">OS:</span> {user?.os ?? "—"}</div>
                                <div><span className="text-blue-700">Language:</span> {user?.language ?? "—"}</div>
                                <div><span className="text-blue-700">Visits:</span> {user?.visitCount ?? "—"}</div>
                                <div><span className="text-blue-700">Last visit:</span> {user?.lastVisit ?? "—"}</div>
                              </div>
                            </div>

                            <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-3">
                              <div className="text-xs font-semibold text-blue-800 mb-2">Hardware</div>
                              <div className="text-xs text-slate-900 space-y-1">
                                <div><span className="text-blue-700">Screen:</span> {hw?.screenRes ?? "—"}</div>
                                <div><span className="text-blue-700">CPU cores:</span> {hw?.cpuCores ?? "—"}</div>
                                <div><span className="text-blue-700">GPU:</span> {hw?.gpu ?? "—"}</div>
                                <div><span className="text-blue-700">Battery:</span> {formatBattery(hw?.battery)}</div>
                              </div>
                            </div>

                            <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-3">
                              <div className="text-xs font-semibold text-blue-800 mb-2">Network</div>
                              <div className="text-xs text-slate-900 space-y-1">
                                <div><span className="text-blue-700">Type:</span> {net?.type ?? "—"}</div>
                                <div><span className="text-blue-700">Downlink:</span> {net?.downlink ?? "—"}</div>
                                <div><span className="text-blue-700">Referrer:</span> {net?.referrer ?? "—"}</div>
                              </div>
                            </div>

                            <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-3">
                              <div className="text-xs font-semibold text-blue-800 mb-2">Preferences</div>
                              <div className="text-xs text-slate-900 space-y-1">
                                <div><span className="text-blue-700">Dark mode:</span> {formatDarkMode(prefs?.darkMode)}</div>
                                <div><span className="text-blue-700">Cookies enabled:</span> {prefs?.cookiesEnabled === true ? "true" : prefs?.cookiesEnabled === false ? "false" : "—"}</div>
                              </div>
                            </div>
                          </div>

                          <div className="mt-3">
                            <div className="text-xs font-semibold text-blue-800 mb-2">Raw JSON</div>
                            <pre className="text-[11px] leading-4 bg-slate-950 text-slate-100 rounded-lg p-3 overflow-auto max-h-72 custom-scrollbar">
                              {safeJsonStringify(l)}
                            </pre>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </FadeIn>
  );
}